import React, { useEffect, useState } from 'react';
import { billingService } from '../../services/billingService';
import UsageIndicator from './UsageIndicator';
import PaymentMethodForm from './PaymentMethodForm';
import TrialUpgradeCard from './TrialUpgradeCard';
import './BillingDashboard.css';
import InlineAlert from '../common/InlineAlert';
import StateBlock from '../common/StateBlock';
import ConfirmDialog from '../common/ConfirmDialog';
import { getUserFacingError } from '../../lib/userFacingErrors';

const TIER_LABELS = {
  payg: 'Legacy PAYG',
  starter: 'Starter',
  growth: 'Growth',
  pro: 'Pro',
};

const TIER_ORDER = ['starter', 'growth', 'pro'];

export default function BillingDashboard({ onBillingChanged }) {
  const [status, setStatus] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [pmError, setPmError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [s, inv] = await Promise.all([
        billingService.getBillingStatus(),
        billingService.getInvoices(10),
      ]);
      setStatus(s);
      setInvoices(inv);
    } catch (e) {
      console.error('Failed to load billing data:', e);
      setLoadError(getUserFacingError(e, 'billing-load'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    billingService.getPlans().then(setPlans).catch((e) => {
      console.error('Failed to load plans:', e);
    });
  }, []);

  const handleCancel = async () => {
    setPendingAction({
      type: 'cancel',
      title: 'Cancel subscription?',
      message: 'Your plan will stay active until the end of the current billing period.',
      confirmLabel: 'Cancel subscription',
      tone: 'danger',
    });
  };

  const executeCancel = async () => {
    setCancelLoading(true);
    setActionError(null);
    try {
      await billingService.cancelSubscription();
      setPendingAction(null);
      await refresh();
    } catch (e) {
      console.error('Cancel failed:', e);
      setActionError(getUserFacingError(e, 'billing-action'));
    } finally {
      setCancelLoading(false);
    }
  };

  const handleChangePlan = async (tier) => {
    const currentTier = status?.subscription?.plan_tier;
    const pendingTier = status?.subscription?.pending_plan_tier;
    const currentTierIdx = Math.max(TIER_ORDER.indexOf(currentTier), -1);
    const tierLabel = TIER_LABELS[tier] || tier;
    const currentLabel = TIER_LABELS[currentTier] || currentTier;
    const isCancelPending = pendingTier && tier === currentTier;
    const direction = TIER_ORDER.indexOf(tier) > currentTierIdx ? 'Upgrade' : 'Downgrade';
    const tierIdx = TIER_ORDER.indexOf(tier);
    setPendingAction({
      type: 'plan',
      tier,
      title: isCancelPending ? `Keep ${currentLabel}?` : `${direction} to ${tierLabel}?`,
      message: isCancelPending
        ? `This cancels your scheduled change to ${TIER_LABELS[pendingTier] || pendingTier}.`
        : `You are currently on ${currentLabel}. This change is scheduled for your next billing period.`,
      confirmLabel: isCancelPending ? 'Keep current plan' : direction,
      tone: tierIdx > currentTierIdx ? 'primary' : 'danger',
    });
  };

  const executePlanChange = async (tier) => {
    setUpgradeLoading(tier);
    setActionError(null);
    try {
      await billingService.updateSubscription(tier);
      setPendingAction(null);
      await refresh();
      await onBillingChanged?.();
    } catch (e) {
      console.error('Plan change failed:', e);
      setActionError(getUserFacingError(e, 'billing-action'));
    } finally {
      setUpgradeLoading(null);
    }
  };

  if (loading) return <div className="billing-dash-loading">Loading billing...</div>;
  if (loadError || !status) {
    const error = loadError || getUserFacingError(null, 'billing-load');
    return (
      <StateBlock
        state="error"
        title={error.title}
        message={error.message}
        actionLabel={error.actionLabel || 'Retry'}
        onAction={refresh}
      />
    );
  }

  const sub = status.subscription;
  const pm = status.default_payment_method;
  const currentTier = sub?.plan_tier;
  const currentTierIdx = TIER_ORDER.indexOf(currentTier);

  if (status.billing_status === 'trialing') {
    return (
      <div className="billing-dashboard billing-dashboard-trial">
        <TrialUpgradeCard billingStatus={status} />
      </div>
    );
  }

  return (
    <div className="billing-dashboard">
      {/* Current Plan */}
      <section className="billing-section">
        <h3>Current Plan</h3>
        {sub ? (
          <div className="billing-plan-info">
            <div className="plan-tier-label">{TIER_LABELS[sub.plan_tier] || sub.plan_tier}</div>
            {sub.plan_tier === 'payg' ? (
              <div className="plan-price">$0.25 per prompt &middot; no monthly fee</div>
            ) : (
              <>
                <div className="plan-price">
                  ${(sub.price_cents / 100).toFixed(2)}/month &middot; {sub.monthly_prompt_quota} prompts included
                </div>
                <div className="plan-overage-note">$0.25 per prompt after quota</div>
              </>
            )}
            {sub.pending_plan_tier && !sub.cancel_at_period_end && (
              <div className="plan-pending">
                Changing to {TIER_LABELS[sub.pending_plan_tier] || sub.pending_plan_tier} on{' '}
                {new Date(sub.current_period_end).toLocaleDateString()}
              </div>
            )}
            {sub.cancel_at_period_end && (
              <div className="plan-canceling">
                Canceling on {new Date(sub.current_period_end).toLocaleDateString()}
              </div>
            )}
            <div className="plan-period">
              Period: {new Date(sub.current_period_start).toLocaleDateString()} &ndash;{' '}
              {new Date(sub.current_period_end).toLocaleDateString()}
            </div>
          </div>
        ) : (
          <div className="no-subscription">No active subscription</div>
        )}

      </section>

      {/* Usage */}
      {status.usage && (
        <section className="billing-section">
          <h3>Usage This Period</h3>
          <UsageIndicator usage={status.usage} />
        </section>
      )}

      {/* Plans */}
      {plans.length > 0 && (
        <section className="billing-section">
          <h3>Plans</h3>
          <div className="billing-plans-grid">
            {plans.map((p) => {
              const isCurrentTier = p.tier === currentTier;
              const isPendingTier = p.tier === sub?.pending_plan_tier;
              const canCancelPending = isCurrentTier && Boolean(sub?.pending_plan_tier);
              const idx = TIER_ORDER.indexOf(p.tier);
              let btnLabel;
              if (upgradeLoading === p.tier) btnLabel = 'Updating...';
              else if (canCancelPending) btnLabel = 'Keep Current Plan';
              else if (isCurrentTier) btnLabel = 'Current Plan';
              else if (isPendingTier) btnLabel = 'Scheduled';
              else if (!sub) btnLabel = 'Select';
              else if (idx > currentTierIdx) btnLabel = 'Upgrade';
              else btnLabel = 'Downgrade';

              return (
                <div key={p.tier} className={`billing-plan-card${isCurrentTier ? ' current' : ''}${isPendingTier ? ' pending' : ''}`}>
                  <div className="billing-plan-card-name">{p.name}</div>
                  <div className="billing-plan-card-price">
                    ${(p.price_cents / 100).toFixed(2)}/mo
                  </div>
                  <div className="billing-plan-card-quota">
                    {p.monthly_prompt_quota} prompts/mo
                  </div>
                  <button
                    className={`billing-btn ${isCurrentTier ? 'keep' : idx > currentTierIdx ? 'upgrade' : 'downgrade'}`}
                    onClick={() => handleChangePlan(p.tier)}
                    disabled={(!canCancelPending && (isCurrentTier || isPendingTier)) || upgradeLoading !== null}
                  >
                    {btnLabel}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Payment Method */}
      <section className="billing-section">
        <h3>Payment Method</h3>
        {pm ? (
          <div className="payment-method-display">
            <span className="pm-brand">{pm.brand}</span>
            <span className="pm-last4">**** {pm.last4}</span>
            <span className="pm-expiry">Exp {pm.exp_month}/{pm.exp_year}</span>
          </div>
        ) : (
          <div className="no-pm">No payment method on file</div>
        )}
        {showPaymentForm ? (
          <div className="pm-update-form">
            <PaymentMethodForm
              onSuccess={() => {
                setShowPaymentForm(false);
                setPmError(null);
                refresh();
              }}
              onError={(e) => setPmError(getUserFacingError(e, 'payment-method'))}
              submitLabel="Update Card"
            />
            {pmError && <InlineAlert compact title={pmError.title} message={pmError.message} />}
            <button
              className="billing-btn cancel-link"
              onClick={() => { setShowPaymentForm(false); setPmError(null); }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            className="billing-btn upgrade pm-update-btn"
            onClick={() => setShowPaymentForm(true)}
          >
            Update Payment Method
          </button>
        )}
      </section>

      {/* Invoices */}
      {invoices.length > 0 && (
        <section className="billing-section">
          <h3>Invoice History</h3>
          <div className="invoices-wrapper">
            <table className="invoices-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>PDF</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td>${(inv.amount_due_cents / 100).toFixed(2)}</td>
                    <td>
                      <span className={`invoice-status ${inv.status}`}>{inv.status}</span>
                    </td>
                    <td>
                      {inv.invoice_pdf_url ? (
                        <a href={inv.invoice_pdf_url} target="_blank" rel="noreferrer">
                          Download
                        </a>
                      ) : (
                        '-'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {actionError && <InlineAlert title={actionError.title} message={actionError.message} />}

      {/* Cancel — intentionally last for maximum friction */}
      {sub && sub.status === 'active' && !sub.cancel_at_period_end && (
        <div className="billing-cancel-zone">
          <button
            className="billing-btn cancel"
            onClick={handleCancel}
            disabled={cancelLoading}
          >
            {cancelLoading ? 'Canceling...' : 'Cancel Subscription'}
          </button>
        </div>
      )}
      <ConfirmDialog
        open={Boolean(pendingAction)}
        title={pendingAction?.title}
        message={pendingAction?.message}
        confirmLabel={pendingAction?.confirmLabel}
        cancelLabel="Go back"
        tone={pendingAction?.tone || 'danger'}
        loading={cancelLoading || upgradeLoading !== null}
        onCancel={() => setPendingAction(null)}
        onConfirm={() => {
          if (pendingAction?.type === 'cancel') executeCancel();
          if (pendingAction?.type === 'plan') executePlanChange(pendingAction.tier);
        }}
      />
    </div>
  );
}
